import React, { useState, useMemo, useRef, useEffect } from "react";
import Papa from "papaparse";

const DISPLAY_COLUMNS = [
  "address", "addressCity", "addressState", "agentEmail", "agentLicenseNumber", "agentName", "agentPhoneNumber",
  "brokerName", "brokerPhoneNumber", "coAgentName", "coAgentNumber",
  "hdpData.homeInfo.homeStatus", "hdpData.homeInfo.homeType",
  "hdpData.homeInfo.price", "hdpData.homeInfo.priceChange",
  "hdpData.homeInfo.priceForHDP", "hdpData.homeInfo.rentZestimate",
  "hdpData.homeInfo.taxAssessedValue", "hdpData.homeInfo.zestimate",
  "listingAgents", "listingAttributionContact", "listingOffices",
  "perplexity_email", "pgapt", "price", "prop_stream_mail_found",
  "propstream_data.agent_email", "propstream_data.apn_info",
  "propstream_data.interest_rates", "propstream_data.legal_description",
  "propstream_data.loan_balances", "propstream_data.loan_payments",
  "propstream_data.loan_types", "propstream_data.owner_name",
  "scraped_agent_result", "zpid",
];

// Format price for display (e.g., $100000 -> $100K)
const formatPrice = (value) => {
  if (value >= 1000000) return `$${value / 1000000}M`;
  return `$${value / 1000}K`;
};

// Format interest rate for display (e.g., 0.05 -> 5.0%)
const formatInterestRate = (value) => `${(value * 100).toFixed(1)}%`;

function ListingsPage() {
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("All");
  const [priceRange, setPriceRange] = useState({ min: 10000, max: 1000000 });
  const [interestRange, setInterestRange] = useState({ min: 0, max: 0.1 });
  const [pgaptFilter, setPgaptFilter] = useState("Clear");
  const [loanTypeFilter, setLoanTypeFilter] = useState("Clear");
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [showAdvancedDropdown, setShowAdvancedDropdown] = useState(false);
  const priceDropdownRef = useRef(null);
  const advancedDropdownRef = useRef(null);
  const rowsPerPage = 12;

  // Fixed price and interest rate limits
  const priceLimits = { min: 10000, max: 1000000, step: 10000 };
  const interestLimits = { min: 0, max: 0.1, step: 0.001 };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(e.target)) {
        setShowPriceDropdown(false);
      }
      if (advancedDropdownRef.current && !advancedDropdownRef.current.contains(e.target)) {
        setShowAdvancedDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle price range change
  const handlePriceChange = (type, value) => {
    const numValue = Number(value);
    setPriceRange((prev) => {
      if (type === "min") {
        return { min: Math.min(numValue, prev.max - priceLimits.step), max: prev.max };
      } else {
        return { min: prev.min, max: Math.max(numValue, prev.min + priceLimits.step) };
      }
    });
    setCurrentPage(1);
  };

  // Handle interest rate range change
  const handleInterestChange = (type, value) => {
    const numValue = Number(value);
    setInterestRange((prev) => {
      if (type === "min") {
        return { min: Math.min(numValue, prev.max - interestLimits.step), max: prev.max };
      } else {
        return { min: prev.min, max: Math.max(numValue, prev.min + interestLimits.step) };
      }
    });
    setCurrentPage(1);
  };

  // Reset price range
  const resetPriceRange = () => {
    setPriceRange({ min: priceLimits.min, max: priceLimits.max });
    setCurrentPage(1);
  };

  // Reset advanced options
  const resetAdvancedOptions = () => {
    setInterestRange({ min: interestLimits.min, max: interestLimits.max });
    setPgaptFilter("Clear");
    setLoanTypeFilter("Clear");
    setCurrentPage(1);
  };

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {};
    DISPLAY_COLUMNS.forEach((col) => {
      let maxLength = col.length;
      data.forEach((row) => {
        const value = row[col]
          ? String(
              [
                "price", "hdpData.homeInfo.price", "hdpData.homeInfo.priceForHDP",
                "hdpData.homeInfo.zestimate", "hdpData.homeInfo.taxAssessedValue",
                "hdpData.homeInfo.rentZestimate", "propstream_data.loan_balances",
                "propstream_data.loan_payments",
              ].includes(col) && row[col] !== "-"
                ? `$${parseFloat(row[col].replace(/[$,]/g, "")).toLocaleString()}`
                : col === "hdpData.homeInfo.priceChange" && row[col] !== "-"
                ? `${parseFloat(row[col]) > 0 ? "+" : ""}$${parseFloat(row[col]).toLocaleString()}`
                : col === "propstream_data.interest_rates" && row[col] !== "-"
                ? `${(parseFloat(row[col]) * 100).toFixed(2)}%`
                : row[col]
            )
          : "-";
        maxLength = Math.max(maxLength, value.length);
      });
      const multiplier = ["listingAgents", "listingOffices", "propstream_data.legal_description"].includes(col) ? 3 : 3.5;
      widths[col] = `${Math.max(maxLength * multiplier, 50)}px`;
    });
    return widths;
  }, [data]);

  // Filter data based on search term, column, price, interest, pgapt, and loan type
  const filteredData = useMemo(() => {
    let result = data;
    // Apply price filter
    if (priceRange.min !== priceLimits.min || priceRange.max !== priceLimits.max) {
      result = result.filter((row) => {
        const price = parseFloat(row.price?.replace(/[$,]/g, "") || 0);
        return price >= priceRange.min && price <= priceRange.max;
      });
    }
    // Apply interest rate filter
    if (interestRange.min !== interestLimits.min || interestRange.max !== interestLimits.max) {
      result = result.filter((row) => {
        const interest = parseFloat(row["propstream_data.interest_rates"] || 0);
        return interest >= interestRange.min && interest <= interestRange.max;
      });
    }
    // Apply pgapt filter
    if (pgaptFilter !== "Clear") {
      result = result.filter((row) => row.pgapt === pgaptFilter);
    }
    // Apply loan type filter
    if (loanTypeFilter !== "Clear") {
      result = result.filter((row) => row["propstream_data.loan_types"] === loanTypeFilter);
    }
    // Apply search term filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter((row) => {
        if (searchColumn === "All") {
          return DISPLAY_COLUMNS.some((col) => {
            const value = row[col]
              ? String(
                  [
                    "price", "hdpData.homeInfo.price", "hdpData.homeInfo.priceForHDP",
                    "hdpData.homeInfo.zestimate", "hdpData.homeInfo.taxAssessedValue",
                    "hdpData.homeInfo.rentZestimate", "propstream_data.loan_balances",
                    "propstream_data.loan_payments",
                  ].includes(col) && row[col] !== "-"
                    ? `$${parseFloat(row[col].replace(/[$,]/g, "")).toLocaleString()}`
                    : col === "hdpData.homeInfo.priceChange" && row[col] !== "-"
                    ? `${parseFloat(row[col]) > 0 ? "+" : ""}$${parseFloat(row[col]).toLocaleString()}`
                    : col === "propstream_data.interest_rates" && row[col] !== "-"
                    ? `${(parseFloat(row[col]) * 100).toFixed(2)}%`
                    : row[col]
                )
              : "-";
            return value.toLowerCase().includes(lowerSearch);
          });
        } else {
          const value = row[searchColumn]
            ? String(
                [
                  "price", "hdpData.homeInfo.price", "hdpData.homeInfo.priceForHDP",
                  "hdpData.homeInfo.zestimate", "hdpData.homeInfo.taxAssessedValue",
                  "hdpData.homeInfo.rentZestimate", "propstream_data.loan_balances",
                  "propstream_data.loan_payments",
                ].includes(searchColumn) && row[searchColumn] !== "-"
                  ? `$${parseFloat(row[searchColumn].replace(/[$,]/g, "")).toLocaleString()}`
                  : searchColumn === "hdpData.homeInfo.priceChange" && row[searchColumn] !== "-"
                  ? `${parseFloat(row[searchColumn]) > 0 ? "+" : ""}$${parseFloat(row[searchColumn]).toLocaleString()}`
                  : searchColumn === "propstream_data.interest_rates" && row[searchColumn] !== "-"
                  ? `${(parseFloat(row[searchColumn]) * 100).toFixed(2)}%`
                  : row[searchColumn]
              )
            : "-";
          return value.toLowerCase().includes(lowerSearch);
        }
      });
    }
    return result;
  }, [data, searchTerm, searchColumn, priceRange, interestRange, pgaptFilter, loanTypeFilter]);

  // Calculate paginated data
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage]);

  // Handle page navigation
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle column selection
  const handleColumnChange = (e) => {
    setSearchColumn(e.target.value);
    setCurrentPage(1);
  };

  // Handle pgapt filter
  const handlePgaptChange = (e) => {
    setPgaptFilter(e.target.value);
    setCurrentPage(1);
  };

  // Handle loan type filter
  const handleLoanTypeChange = (e) => {
    setLoanTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  // Handle CSV file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      setError("Please upload a valid CSV file.");
      return;
    }
    setLoading(true);
    setError("");
    setCurrentPage(1);
    setSearchTerm("");
    setSearchColumn("All");
    setPriceRange({ min: priceLimits.min, max: priceLimits.max });
    setInterestRange({ min: interestLimits.min, max: interestLimits.max });
    setPgaptFilter("Clear");
    setLoanTypeFilter("Clear");
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length) {
          setError("Error parsing CSV file. Please check the file format.");
          setData([]);
        } else {
          const normalizedData = result.data.map((row) =>
            DISPLAY_COLUMNS.reduce((acc, col) => ({ ...acc, [col]: row[col] || "-" }), {})
          );
          setData(normalizedData);
        }
        setLoading(false);
      },
      error: () => {
        setError("Failed to read the CSV file.");
        setLoading(false);
      },
    });
  };

  // Handle export to CSV with file save dialog
  const handleExportCSV = async () => {
    const exportData = filteredData.map((row) => {
      const formattedRow = {};
      DISPLAY_COLUMNS.forEach((col) => {
        formattedRow[col] = row[col]
          ? [
              "price", "hdpData.homeInfo.price", "hdpData.homeInfo.priceForHDP",
              "hdpData.homeInfo.zestimate", "hdpData.homeInfo.taxAssessedValue",
              "hdpData.homeInfo.rentZestimate", "propstream_data.loan_balances",
              "propstream_data.loan_payments",
            ].includes(col) && row[col] !== "-"
            ? `$${parseFloat(row[col].replace(/[$,]/g, "")).toLocaleString()}`
            : col === "hdpData.homeInfo.priceChange" && row[col] !== "-"
            ? `${parseFloat(row[col]) > 0 ? "+" : ""}$${parseFloat(row[col]).toLocaleString()}`
            : col === "propstream_data.interest_rates" && row[col] !== "-"
            ? `${(parseFloat(row[col]) * 100).toFixed(2)}%`
            : row[col]
          : "-";
      });
      return formattedRow;
    });

    const csv = Papa.unparse(exportData, { columns: DISPLAY_COLUMNS });
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const defaultFilename = `real_estate_listings_${timestamp}.csv`;

    if (window.showSaveFilePicker) {
      try {
        // Prompt user to choose save location and filename
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: defaultFilename,
          types: [
            {
              description: "CSV Files",
              accept: { "text/csv": [".csv"] },
            },
          ],
        });
        const writable = await fileHandle.createWritable();
        await writable.write(csv);
        await writable.close();
      } catch (err) {
        // User cancelled or error occurred; fall back to default download
        console.warn("File picker cancelled or failed:", err);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", defaultFilename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      // File System Access API not supported; use default download
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", defaultFilename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4">
      <div className="w-full max-w-[95vw] bg-white rounded-lg shadow-lg p-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          Real Estate Listings
        </h1>

        <div className="mb-4">
          <label htmlFor="csv-upload" className="block text-sm font-medium text-gray-700 mb-1">
            Upload CSV File
          </label>
          <input
            type="file"
            id="csv-upload"
            accept=".csv"
            onChange={handleFileUpload}
            className="block w-full text-xs text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          {loading && <p className="text-blue-500 text-xs mt-1">Loading...</p>}
        </div>

        <div className="mb-4 flex items-end space-x-2">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search Listings
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search listings..."
              className="block w-full text-xs text-gray-700 border border-gray-300 rounded-md py-1 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="w-32 relative" ref={priceDropdownRef}>
            <label htmlFor="price-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Price Range
            </label>
            <button
              id="price-filter"
              onClick={() => setShowPriceDropdown(!showPriceDropdown)}
              className="block w-full text-xs text-gray-700 border border-gray-300 rounded-md py-1 px-3 bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 truncate"
            >
              {`${formatPrice(priceRange.min)} - ${formatPrice(priceRange.max)}`}
            </button>
            {showPriceDropdown && (
              <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg p-4">
                <div className="mb-2 text-xs font-semibold text-gray-700">
                  {`${formatPrice(priceRange.min)} - ${formatPrice(priceRange.max)}`}
                </div>
                <div className="relative h-4 mb-4">
                  <div className="absolute w-full h-1 bg-gray-200 rounded-lg top-1/2 transform -translate-y-1/2" />
                  <div
                    className="absolute h-1 bg-blue-200 rounded-lg top-1/2 transform -translate-y-1/2"
                    style={{
                      left: `${((priceRange.min - priceLimits.min) / (priceLimits.max - priceLimits.min)) * 100}%`,
                      width: `${((priceRange.max - priceRange.min) / (priceLimits.max - priceLimits.min)) * 100}%`,
                    }}
                  />
                  <input
                    type="range"
                    min={priceLimits.min}
                    max={priceLimits.max}
                    step={priceLimits.step}
                    value={priceRange.min}
                    onChange={(e) => handlePriceChange("min", e.target.value)}
                    className="absolute w-full h-4 bg-transparent appearance-none cursor-pointer"
                    style={{ zIndex: 2 }}
                  />
                  <input
                    type="range"
                    min={priceLimits.min}
                    max={priceLimits.max}
                    step={priceLimits.step}
                    value={priceRange.max}
                    onChange={(e) => handlePriceChange("max", e.target.value)}
                    className="absolute w-full h-4 bg-transparent appearance-none cursor-pointer"
                    style={{ zIndex: 2 }}
                  />
                  <style jsx>{`
                    input[type="range"] {
                      pointer-events: none;
                    }
                    input[type="range"]::-webkit-slider-thumb {
                      pointer-events: auto;
                      appearance: none;
                      width: 12px;
                      height: 12px;
                      background: #2563eb;
                      border-radius: 50%;
                      cursor: pointer;
                      box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(37, 99, 235, 0.3);
                    }
                    input[type="range"]::-moz-range-thumb {
                      pointer-events: auto;
                      width: 12px;
                      height: 12px;
                      background: #2563eb;
                      border-radius: 50%;
                      cursor: pointer;
                      border: none;
                      box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(37, 99, 235, 0.3);
                    }
                    input[type="range"]::-webkit-slider-runnable-track {
                      width: 100%;
                      height: 8px;
                      background: transparent;
                    }
                    input[type="range"]::-moz-range-track {
                      width: 100%;
                      height: 8px;
                      background: transparent;
                    }
                  `}</style>
                </div>
                <button
                  onClick={resetPriceRange}
                  className="mt-3 w-full text-xs text-blue-700 hover:underline"
                >
                  Reset to $10K - $1M
                </button>
              </div>
            )}
          </div>
          <div className="w-32 relative" ref={advancedDropdownRef}>
            <label htmlFor="advanced-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Advanced Options
            </label>
            <button
              id="advanced-filter"
              onClick={() => setShowAdvancedDropdown(!showAdvancedDropdown)}
              className="block w-full text-xs text-gray-700 border border-gray-300 rounded-md py-1 px-3 bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 truncate"
            >
              Advanced Options
            </button>
            {showAdvancedDropdown && (
              <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg p-4">
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-700 mb-2">
                    Interest Rate: {formatInterestRate(interestRange.min)} - {formatInterestRate(interestRange.max)}
                  </div>
                  <div className="relative h-4 mb-4">
                    <div className="absolute w-full h-1 bg-gray-200 rounded-lg top-1/2 transform -translate-y-1/2" />
                    <div
                      className="absolute h-1 bg-blue-200 rounded-lg top-1/2 transform -translate-y-1/2"
                      style={{
                        left: `${((interestRange.min - interestLimits.min) / (interestLimits.max - interestLimits.min)) * 100}%`,
                        width: `${((interestRange.max - interestRange.min) / (interestLimits.max - interestLimits.min)) * 100}%`,
                      }}
                    />
                    <input
                      type="range"
                      min={interestLimits.min}
                      max={interestLimits.max}
                      step={interestLimits.step}
                      value={interestRange.min}
                      onChange={(e) => handleInterestChange("min", e.target.value)}
                      className="absolute w-full h-4 bg-transparent appearance-none cursor-pointer"
                      style={{ zIndex: 2 }}
                    />
                    <input
                      type="range"
                      min={interestLimits.min}
                      max={interestLimits.max}
                      step={interestLimits.step}
                      value={interestRange.max}
                      onChange={(e) => handleInterestChange("max", e.target.value)}
                      className="absolute w-full h-4 bg-transparent appearance-none cursor-pointer"
                      style={{ zIndex: 2 }}
                    />
                    <style jsx>{`
                      input[type="range"] {
                        pointer-events: none;
                      }
                      input[type="range"]::-webkit-slider-thumb {
                        pointer-events: auto;
                        appearance: none;
                        width: 12px;
                        height: 12px;
                        background: #2563eb;
                        border-radius: 50%;
                        cursor: pointer;
                        box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(37, 99, 235, 0.3);
                      }
                      input[type="range"]::-moz-range-thumb {
                        pointer-events: auto;
                        width: 12px;
                        height: 12px;
                        background: #2563eb;
                        border-radius: 50%;
                        cursor: pointer;
                        border: none;
                        box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(37, 99, 235, 0.3);
                      }
                      input[type="range"]::-webkit-slider-runnable-track {
                        width: 100%;
                        height: 8px;
                        background: transparent;
                      }
                      input[type="range"]::-moz-range-track {
                        width: 100%;
                        height: 8px;
                        background: transparent;
                      }
                    `}</style>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Listing Status</div>
                  <label className="block text-xs text-gray-700 mb-1">
                    <input
                      type="radio"
                      name="pgapt"
                      value="ForSale"
                      checked={pgaptFilter === "ForSale"}
                      onChange={handlePgaptChange}
                      className="mr-1 accent-blue-600"
                    />
                    For Sale
                  </label>
                  <label className="block text-xs text-gray-700 mb-1">
                    <input
                      type="radio"
                      name="pgapt"
                      value="ComingSoon"
                      checked={pgaptFilter === "ComingSoon"}
                      onChange={handlePgaptChange}
                      className="mr-1 accent-blue-600"
                    />
                    Coming Soon
                  </label>
                  <label className="block text-xs text-gray-700">
                    <input
                      type="radio"
                      name="pgapt"
                      value="Clear"
                      checked={pgaptFilter === "Clear"}
                      onChange={handlePgaptChange}
                      className="mr-1 accent-blue-600"
                    />
                    Clear
                  </label>
                </div>
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Loan Type</div>
                  <label className="block text-xs text-gray-700 mb-1">
                    <input
                      type="radio"
                      name="loanType"
                      value="New Conventional"
                      checked={loanTypeFilter === "New Conventional"}
                      onChange={handleLoanTypeChange}
                      className="mr-1 accent-blue-600"
                    />
                    New Conventional
                  </label>
                  <label className="block text-xs text-gray-700 mb-1">
                    <input
                      type="radio"
                      name="loanType"
                      value="VA"
                      checked={loanTypeFilter === "VA"}
                      onChange={handleLoanTypeChange}
                      className="mr-1 accent-blue-600"
                    />
                    VA
                  </label>
                  <label className="block text-xs text-gray-700 mb-1">
                    <input
                      type="radio"
                      name="loanType"
                      value="FHA"
                      checked={loanTypeFilter === "FHA"}
                      onChange={handleLoanTypeChange}
                      className="mr-1 accent-blue-600"
                    />
                    FHA
                  </label>
                  <label className="block text-xs text-gray-700">
                    <input
                      type="radio"
                      name="loanType"
                      value="Clear"
                      checked={loanTypeFilter === "Clear"}
                      onChange={handleLoanTypeChange}
                      className="mr-1 accent-blue-600"
                    />
                    Clear
                  </label>
                </div>
                <button
                  onClick={resetAdvancedOptions}
                  className="w-full text-xs text-blue-700 hover:underline"
                >
                  Reset Advanced Options
                </button>
              </div>
            )}
          </div>
          <div className="w-40">
            <label htmlFor="search-column" className="block text-sm font-medium text-gray-700 mb-1">
              Search Column
            </label>
            <select
              id="search-column"
              value={searchColumn}
              onChange={handleColumnChange}
              className="block w-full text-xs text-gray-700 border border-gray-300 rounded-md py-1 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="All">All Columns</option>
              {DISPLAY_COLUMNS.map((col) => (
                <option key={col} value={col}>
                  {col
                    .replace("hdpData.homeInfo.", "")
                    .replace("propstream_data.", "")
                    .replace(/([A-Z])/g, " $1")
                    .trim()
                    .replace("Zestimate", "Zestimate")
                    .replace("Price For H D P", "Price For HDP")
                    .replace("Rent Zestimate", "Rent Zestimate")
                    .replace("Tax Assessed Value", "Tax Assessed Value")
                    .replace("Agent Email", "Agent Email")
                    .replace("Apn Info", "APN Info")
                    .replace("Interest Rates", "Interest Rates")
                    .replace("Legal Description", "Legal Description")
                    .replace("Loan Balances", "Loan Balances")
                    .replace("Loan Payments", "Loan Payments")
                    .replace("Loan Types", "Loan Types")
                    .replace("Owner Name", "Owner Name")
                    .replace("Prop Stream Mail Found", "Prop Stream Mail Found")
                    .replace(/^./, (str) => str.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>

        {data.length > 0 ? (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full text-center border-collapse text-xs">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    {DISPLAY_COLUMNS.map((col, index) => (
                      <th
                        key={col}
                        className={`p-1.5 font-semibold whitespace-nowrap ${
                          index < DISPLAY_COLUMNS.length - 1 ? "border-r border-gray-200" : ""
                        }`}
                        style={{ width: columnWidths[col] }}
                      >
                        {col
                          .replace("hdpData.homeInfo.", "")
                          .replace("propstream_data.", "")
                          .replace(/([A-Z])/g, " $1")
                          .trim()
                          .replace("Zestimate", "Zestimate")
                          .replace("Price For H D P", "Price For HDP")
                          .replace("Rent Zestimate", "Rent Zestimate")
                          .replace("Tax Assessed Value", "Tax Assessed Value")
                          .replace("Agent Email", "Agent Email")
                          .replace("Apn Info", "APN Info")
                          .replace("Interest Rates", "Interest Rates")
                          .replace("Legal Description", "Legal Description")
                          .replace("Loan Balances", "Loan Balances")
                          .replace("Loan Payments", "Loan Payments")
                          .replace("Loan Types", "Loan Types")
                          .replace("Owner Name", "Owner Name")
                          .replace("Prop Stream Mail Found", "Prop Stream Mail Found")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      {DISPLAY_COLUMNS.map((col, colIndex) => (
                        <td
                          key={col}
                          className={`p-1.5 text-gray-700 whitespace-nowrap ${
                            colIndex < DISPLAY_COLUMNS.length - 1 ? "border-r border-gray-200" : ""
                          }`}
                          style={{ width: columnWidths[col] }}
                        >
                          {[
                            "price", "hdpData.homeInfo.price", "hdpData.homeInfo.priceForHDP",
                            "hdpData.homeInfo.zestimate", "hdpData.homeInfo.taxAssessedValue",
                            "hdpData.homeInfo.rentZestimate", "propstream_data.loan_balances",
                            "propstream_data.loan_payments",
                          ].includes(col) && row[col] && row[col] !== "-"
                            ? `$${parseFloat(row[col].replace(/[$,]/g, "")).toLocaleString()}`
                            : col === "hdpData.homeInfo.priceChange" && row[col] && row[col] !== "-"
                            ? `${parseFloat(row[col]) > 0 ? "+" : ""}$${parseFloat(row[col]).toLocaleString()}`
                            : col === "propstream_data.interest_rates" && row[col] && row[col] !== "-"
                            ? `${(parseFloat(row[col]) * 100).toFixed(2)}%`
                            : row[col]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredData.length > 0 && (
              <div className="mt-4 flex flex-col items-center space-y-4">
                <div className="flex justify-center items-center space-x-2 text-xs">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md font-semibold ${
                      currentPage === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                    }`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2))
                    .map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 py-1 rounded-md ${
                          currentPage === page ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md font-semibold ${
                      currentPage === totalPages ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                    }`}
                  >
                    Next
                  </button>
                </div>
                <button
                  onClick={handleExportCSV}
                  disabled={filteredData.length === 0}
                  className={`px-4 py-2 rounded-md font-semibold text-sm text-white ${
                    filteredData.length === 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  Export to CSV
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500 text-xs mt-4">
            Please upload a CSV file to view data.
          </p>
        )}
      </div>
    </div>
  );
}

export default ListingsPage;